import { apFirst as apFirst_, apS as apS_, apSecond as apSecond_ } from 'fp-ts/Apply';
import * as E from 'fp-ts/Either';
import { eqStrict } from 'fp-ts/Eq';
import { ask as ask_, asks as asks_, fromReaderK as fromReaderK_ } from 'fp-ts/FromReader';
import { flow, identity, pipe } from 'fp-ts/function';
import { bindTo as bindTo_, flap as flap_ } from 'fp-ts/Functor';
import * as O from 'fp-ts/Option';
import * as R from 'fp-ts/Reader';
import * as RT from 'fp-ts/ReaderT';
import { bufferTime as rxBufferTime, delay as rxDelay, throttleTime as rxThrottleTime, toArray, withLatestFrom as rxWithLatestFrom } from 'rxjs/operators';
import * as T from '../Observable';
// -------------------------------------------------------------------------------------
// natural transformations
// -------------------------------------------------------------------------------------
/**
 * @category constructors
 */
export var fromObservable = R.of;
/**
 * @category constructors
 */
export var fromOption = flow(T.fromOption, fromObservable);
/**
 * @category constructors
 */
export var fromReadonlyArray = flow(T.fromReadonlyArray, fromObservable);
/**
 * @category constructors
 */
export var fromReader = RT.fromReader(T.Pointed);
/**
 * @category constructors
 */
export var fromIO = 
/*#__PURE__*/
flow(T.fromIO, fromObservable);
/**
 * @category constructors
 */
export var fromTask = 
/*#__PURE__*/
flow(T.fromTask, fromObservable);
/**
 * @category constructors
 */
export var fromTaskOption = flow(T.fromTaskOption, fromObservable);
/**
 * @category constructors
 */
export var fromReaderTask = function (ma) { return flow(ma, T.fromTask); };
/**
 * @category constructors
 */
export var fromPredicate = function (predicate) {
    return function (a) {
        return predicate(a) ? of(a) : zero();
    };
};
// -------------------------------------------------------------------------------------
// combinators
// -------------------------------------------------------------------------------------
/**
 * @category combinators
 */
export var fromOptionK = function (f) { return flow(f, fromOption); };
/**
 * @category combinators
 */
export var fromTaskOptionK = function (f) { return flow(f, fromTaskOption); };
/**
 * Changes the value of the local context during the execution of the action `ma` (similar to `Contravariant`'s
 * `contramap`).
 *
 * @category combinators
 */
export var local = R.local;
/**
 * Less strict version of [`asksReaderTask`](#asksreadertask).
 *
 * @category combinators
 */
export var asksReaderObservableW = R.asksReaderW;
/**
 * Effectfully accesses the environment.
 *
 * @category combinators
 */
export var asksReaderObservable = asksReaderObservableW;
// -------------------------------------------------------------------------------------
// type class members
// -------------------------------------------------------------------------------------
var _map = function (fa, f) { return pipe(fa, map(f)); };
var _ap = function (fab, fa) { return pipe(fab, ap(fa)); };
/* istanbul ignore next */
var _alt = function (fx, f) { return pipe(fx, alt(f)); };
/* istanbul ignore next */
var _filter = function (fa, p) { return pipe(fa, filter(p)); };
/* istanbul ignore next */
var _filterMap = function (fa, f) { return pipe(fa, filterMap(f)); };
/* istanbul ignore next */
var _partition = function (fa, p) { return pipe(fa, partition(p)); };
/* istanbul ignore next */
var _partitionMap = function (fa, f) { return pipe(fa, partitionMap(f)); };
/**
 * `map` can be used to turn functions `(a: A) => B` into functions `(fa: F<A>) => F<B>` whose argument and return types
 * use the type constructor `F` to represent some computational context.
 *
 * @category Functor
 */
export var map = RT.map(T.Functor);
/**
 * Apply a function to an argument under a type constructor.
 *
 * @category Apply
 */
export var ap = RT.ap(T.Apply);
/**
 * Less strict version of [`ap`](#ap).
 *
 * @category Apply
 */
export var apW = ap;
/**
 * @category Pointed
 */
export var of = 
/*#__PURE__*/
RT.of(T.Pointed);
/**
 * Identifies an associative operation on a type constructor. It is similar to `Semigroup`, except that it applies to
 * types of kind `* -> *`.
 *
 * @category Alt
 */
export var alt = function (that) { return function (me) { return function (r) {
    return pipe(me(r), T.alt(function () { return that()(r); }));
}; }; };
export var zero = function () { return T.zero; };
/**
 * @category Filterable
 */
export var filterMap = function (f) { return function (fa) { return function (r) {
    return pipe(fa(r), T.filterMap(f));
}; }; };
/**
 * @category Compactable
 */
export var compact = 
/*#__PURE__*/
filterMap(identity);
/**
 * @category Filterable
 */
export var partitionMap = function (f) { return function (fa) { return ({
    left: pipe(fa, filterMap(function (a) { return O.fromEither(E.swap(f(a))); })),
    right: pipe(fa, filterMap(function (a) { return O.fromEither(f(a)); }))
}); }; };
/**
 * @category Compactable
 */
export var separate = 
/*#__PURE__*/
partitionMap(identity);
/**
 * @category Filterable
 */
export var filter = function (predicate) {
    return filterMap(O.fromPredicate(predicate));
};
/**
 * @category Filterable
 */
export var partition = function (predicate) {
    return partitionMap(E.fromPredicate(predicate, identity));
};
export var dropLeft = function (n) { return function (ma) {
    return flow(ma, T.dropLeft(n));
}; };
export var dropRight = function (n) { return function (ma) {
    return flow(ma, T.dropRight(n));
}; };
export function dropLeftWhile(predicate) {
    return function (ma) { return flow(ma, T.dropLeftWhile(predicate)); };
}
// -------------------------------------------------------------------------------------
// instances
// -------------------------------------------------------------------------------------
/**
 * @category instances
 */
export var URI = 'ReaderObservable';
/**
 * @category instances
 */
export var Functor = {
    URI: URI,
    map: _map
};
/**
 * Derivable from `Functor`.
 *
 * @category combinators
 */
export var flap = 
/*#__PURE__*/
flap_(Functor);
/**
 * @category instances
 */
export var Pointed = {
    URI: URI,
    of: of
};
/**
 * @category instances
 */
export var Apply = {
    URI: URI,
    map: _map,
    ap: _ap
};
/**
 * Combine two effectful actions, keeping only the result of the first.
 *
 * Derivable from `Apply`.
 *
 * @category combinators
 */
export var apFirst = 
/*#__PURE__*/
apFirst_(Apply);
/**
 * Combine two effectful actions, keeping only the result of the second.
 *
 * Derivable from `Apply`.
 *
 * @category combinators
 */
export var apSecond = 
/*#__PURE__*/
apSecond_(Apply);
/**
 * @category instances
 */
export var Applicative = {
    URI: URI,
    map: _map,
    ap: _ap,
    of: of
};
/**
 * @category instances
 */
export var Alt = {
    URI: URI,
    map: _map,
    alt: _alt
};
/**
 * @category instances
 */
export var Alternative = {
    URI: URI,
    map: _map,
    ap: _ap,
    of: of,
    alt: _alt,
    zero: zero
};
/**
 * @category instances
 */
export var Compactable = {
    URI: URI,
    compact: compact,
    separate: separate
};
/**
 * @category instances
 */
export var Filterable = {
    URI: URI,
    compact: compact,
    separate: separate,
    map: _map,
    filter: _filter,
    filterMap: _filterMap,
    partition: _partition,
    partitionMap: _partitionMap
};
/**
 * @category instances
 */
export var FromIO = {
    URI: URI,
    fromIO: fromIO
};
/**
 * @category combinators
 */
export var fromIOK = function (f) {
    return function () {
        var a = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            a[_i] = arguments[_i];
        }
        return fromIO(f.apply(void 0, a));
    };
};
/**
 * @category instances
 */
export var FromReader = {
    URI: URI,
    fromReader: fromReader
};
/**
 * Reads the current context.
 *
 * @category constructors
 */
export var ask = 
/*#__PURE__*/
ask_(FromReader);
/**
 * Projects a value from the global context in a `ReaderTask`.
 *
 * @category constructors
 */
export var asks = 
/*#__PURE__*/
asks_(FromReader);
/**
 * @category combinators
 */
export var fromReaderK = 
/*#__PURE__*/
fromReaderK_(FromReader);
/**
 * @category instances
 */
export var FromTask = {
    URI: URI,
    fromIO: fromIO,
    fromTask: fromTask
};
/**
 * @category combinators
 */
export var fromTaskK = function (f) {
    return function () {
        var a = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            a[_i] = arguments[_i];
        }
        return fromTask(f.apply(void 0, a));
    };
};
/**
 * @category combinators
 */
export var fromReaderTaskK = function (f) {
    return function () {
        var a = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            a[_i] = arguments[_i];
        }
        return fromReaderTask(f.apply(void 0, a));
    };
};
export var FromObservable = {
    URI: URI,
    fromIO: fromIO,
    fromTask: fromTask,
    fromObservable: fromObservable
};
/**
 * @category combinators
 */
export var fromObservableK = function (f) {
    return function () {
        var a = [];
        for (var _i = 0; _i < arguments.length; _i++) {
            a[_i] = arguments[_i];
        }
        return fromObservable(f.apply(void 0, a));
    };
};
// -------------------------------------------------------------------------------------
// do notation
// -------------------------------------------------------------------------------------
/**
 */
export var Do = 
/*#__PURE__*/
of({});
/**
 */
export var bindTo = 
/*#__PURE__*/
bindTo_(Functor);
// -------------------------------------------------------------------------------------
// pipeable sequence S
// -------------------------------------------------------------------------------------
/**
 */
export var apS = 
/*#__PURE__*/
apS_(Apply);
/**
 */
export var apSW = apS;
// -------------------------------------------------------------------------------------
// sequence T
// -------------------------------------------------------------------------------------
/**
 */
export var ApT = 
/*#__PURE__*/
of([]);
// -------------------------------------------------------------------------------------
// utils
// -------------------------------------------------------------------------------------
export function toReaderTask(ma) {
    return flow(ma, T.toTask);
}
export function toReaderTaskOption(o) {
    return flow(o, T.toTaskOption);
}
export function toReadonlyArray(ma) {
    return flow(ma, toArray());
}
export function bufferTime(millis) {
    return function (ma) { return flow(ma, rxBufferTime(millis)); };
}
export function delay(millis) {
    return function (ma) { return flow(ma, rxDelay(millis)); };
}
export function distinctUntilChanged(eq) {
    if (eq === void 0) { eq = eqStrict; }
    return function (ma) { return flow(ma, T.distinctUntilChanged(eq)); };
}
export function head(o) {
    return flow(o, T.head);
}
export function throttleTime(millis, config, scheduler) {
    return function (ma) { return flow(ma, rxThrottleTime(millis, scheduler, config)); };
}
export function withLatestFrom(mb, f) {
    return function (ma) { return function (r) { return rxWithLatestFrom(mb(r), f)(ma(r)); }; };
}
